import { useState, useEffect } from "react";
import { toast } from "sonner";
import { 
  Settings, 
  Mail, 
  Server, 
  Key, 
  Globe, 
  Check, 
  X, 
  RefreshCw,
  Shield,
  Zap,
  ExternalLink,
  Copy,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emailProviders = [
  {
    id: "mail_tm",
    name: "Mail.tm",
    description: "Бесплатный API для временных почтовых ящиков",
    website: "https://mail.tm",
    features: ["Бесплатно", "REST API", "Автоудаление"],
    fields: [
      { key: "api_url", label: "API URL", default: "https://api.mail.tm", type: "text" }
    ]
  },
  {
    id: "guerrilla",
    name: "Guerrilla Mail",
    description: "Популярный сервис одноразовых почтовых ящиков",
    website: "https://www.guerrillamail.com",
    features: ["Бесплатно", "Множество доменов", "60 мин хранение"],
    fields: [
      { key: "api_url", label: "API URL", default: "https://api.guerrillamail.com/ajax.php", type: "text" }
    ]
  },
  {
    id: "custom_smtp",
    name: "Собственный SMTP/IMAP",
    description: "Настройка собственного почтового сервера",
    website: "",
    features: ["Полный контроль", "Свои домены", "Без лимитов"],
    fields: [
      { key: "smtp_host", label: "SMTP Хост", default: "", type: "text" },
      { key: "smtp_port", label: "SMTP Порт", default: "587", type: "number" },
      { key: "imap_host", label: "IMAP Хост", default: "", type: "text" },
      { key: "imap_port", label: "IMAP Порт", default: "993", type: "number" },
      { key: "username", label: "Логин", default: "", type: "text" },
      { key: "password", label: "Пароль", default: "", type: "password" },
      { key: "domain", label: "Домен", default: "", type: "text" }
    ]
  },
  {
    id: "mailgun",
    name: "Mailgun",
    description: "Профессиональный API для отправки и получения почты",
    website: "https://www.mailgun.com",
    features: ["10,000 писем бесплатно", "API ключ", "Webhooks"],
    fields: [
      { key: "api_key", label: "API Key", default: "", type: "password" },
      { key: "domain", label: "Домен", default: "", type: "text" },
      { key: "region", label: "Регион", default: "US", type: "select", options: ["US", "EU"] }
    ]
  }
];

export default function AdminIntegrations() {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState("mail_tm");
  const [providerConfig, setProviderConfig] = useState({});
  const [showPasswords, setShowPasswords] = useState({});
  const [testResult, setTestResult] = useState(null);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/integrations/email`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        if (data) {
          setSelectedProvider(data.provider || "mail_tm");
          setProviderConfig(data.config || {});
          setSettings(data);
        }
      }
    } catch (err) {
      console.error("Error fetching settings:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/integrations/email`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          provider: selectedProvider,
          config: providerConfig,
          is_active: true
        })
      });
      
      if (res.ok) {
        toast.success("Настройки сохранены");
        fetchSettings();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Ошибка сохранения");
      }
    } catch (err) {
      toast.error("Ошибка сети");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(`${API_URL}/api/admin/integrations/email/test`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          provider: selectedProvider,
          config: providerConfig
        })
      });
      
      const data = await res.json();
      setTestResult(data);
      
      if (data.success) {
        toast.success("Подключение успешно!");
      } else {
        toast.error(data.error || "Ошибка подключения");
      }
    } catch (err) {
      setTestResult({ success: false, error: "Ошибка сети" });
      toast.error("Ошибка сети");
    } finally {
      setTesting(false);
    }
  };

  const currentProvider = emailProviders.find(p => p.id === selectedProvider);

  const togglePasswordVisibility = (key) => {
    setShowPasswords(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Скопировано");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-integrations-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl shadow-lg">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Интеграции</h1>
            <p className="text-sm text-slate-500">Настройка внешних сервисов</p>
          </div>
        </div>
      </div>

      {/* Email Provider Selection */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-200 bg-slate-50">
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-slate-600" />
            <h2 className="font-semibold text-slate-800">Временная почта</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1">Выберите провайдера для создания временных почтовых ящиков</p>
        </div>

        <div className="p-6">
          {/* Provider Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {emailProviders.map(provider => (
              <button
                key={provider.id}
                onClick={() => {
                  setSelectedProvider(provider.id);
                  setProviderConfig({});
                  setTestResult(null);
                }}
                className={`p-4 rounded-xl border-2 text-left transition-all ${
                  selectedProvider === provider.id
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-semibold text-slate-800">{provider.name}</h3>
                    <p className="text-sm text-slate-500 mt-1">{provider.description}</p>
                  </div>
                  {selectedProvider === provider.id && (
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex flex-wrap gap-2 mt-3">
                  {provider.features.map((feature, idx) => (
                    <span key={idx} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
                {provider.website && (
                  <a
                    href={provider.website}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 mt-3"
                  >
                    <ExternalLink className="w-3 h-3" />
                    Документация
                  </a>
                )}
              </button>
            ))}
          </div>

          {/* Provider Configuration */}
          {currentProvider && (
            <div className="border-t border-slate-200 pt-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
                <Server className="w-4 h-4" />
                Настройки {currentProvider.name}
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {currentProvider.fields.map(field => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-slate-700 mb-1.5">
                      {field.label}
                    </label>
                    {field.type === "select" ? (
                      <select
                        value={providerConfig[field.key] || field.default}
                        onChange={(e) => setProviderConfig({ ...providerConfig, [field.key]: e.target.value })}
                        className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        {field.options.map(opt => (
                          <option key={opt} value={opt}>{opt}</option>
                        ))}
                      </select>
                    ) : field.type === "password" ? (
                      <div className="relative">
                        <Input
                          type={showPasswords[field.key] ? "text" : "password"}
                          value={providerConfig[field.key] || ""}
                          onChange={(e) => setProviderConfig({ ...providerConfig, [field.key]: e.target.value })}
                          placeholder={field.default || `Введите ${field.label.toLowerCase()}`}
                        />
                        <button
                          type="button"
                          onClick={() => togglePasswordVisibility(field.key)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showPasswords[field.key] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    ) : (
                      <Input
                        type={field.type}
                        value={providerConfig[field.key] || field.default || ""}
                        onChange={(e) => setProviderConfig({ ...providerConfig, [field.key]: e.target.value })}
                        placeholder={field.default || `Введите ${field.label.toLowerCase()}`}
                      />
                    )}
                  </div>
                ))}
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={`mt-4 p-4 rounded-lg ${
                  testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                }`}>
                  <div className="flex items-center gap-2">
                    {testResult.success ? (
                      <>
                        <Check className="w-5 h-5 text-green-600" />
                        <span className="font-medium text-green-800">Подключение успешно</span>
                      </>
                    ) : (
                      <>
                        <X className="w-5 h-5 text-red-600" />
                        <span className="font-medium text-red-800">Ошибка подключения</span>
                      </>
                    )}
                  </div>
                  {testResult.message && (
                    <p className="text-sm mt-2 text-slate-600">{testResult.message}</p>
                  )}
                  {testResult.test_email && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm text-slate-600">Тестовый email:</span>
                      <code className="px-2 py-1 bg-white rounded text-sm">{testResult.test_email}</code>
                      <button
                        onClick={() => copyToClipboard(testResult.test_email)}
                        className="p-1 text-slate-400 hover:text-slate-600"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-3 mt-6">
                <Button
                  onClick={handleTest}
                  variant="outline"
                  disabled={testing}
                  data-testid="test-connection-btn"
                >
                  {testing ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Zap className="w-4 h-4 mr-2" />
                  )}
                  Тестировать
                </Button>
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Check className="w-4 h-4 mr-2" />
                  )}
                  Сохранить
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Current Status */}
      {settings && settings.is_active && (
        <div className="bg-white rounded-xl border border-slate-200 p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <h3 className="font-semibold text-slate-800">Текущая конфигурация</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Провайдер</p>
              <p className="font-medium text-slate-800">{emailProviders.find(p => p.id === settings.provider)?.name || settings.provider}</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Статус</p>
              <p className="font-medium text-green-600">Активен</p>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-xs text-slate-500">Обновлено</p>
              <p className="font-medium text-slate-800">{new Date(settings.updated_at).toLocaleDateString("ru-RU")}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
